import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { Heart, X, MapPin, MessageCircle, Sparkles, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const Card = ({ user, onSwipe, top }) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const likeOpacity = useTransform(x, [50, 150], [0, 1]);
  const nopeOpacity = useTransform(x, [-150, -50], [1, 0]);

  const photo = user.dating_photos?.[0] || user.photo_url || `https://api.dicebear.com/7.x/personas/svg?seed=${user.id}`;

  return (
    <motion.div
      style={{ x, rotate }}
      drag={top ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={(e, info) => {
        if (info.offset.x > 120) onSwipe(true);
        else if (info.offset.x < -120) onSwipe(false);
      }}
      className="absolute inset-0 rounded-3xl overflow-hidden bg-[#131A26] border border-white/15 shadow-2xl select-none"
    >
      <img src={photo} alt={user.name} className="w-full h-full object-cover pointer-events-none" draggable={false} />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

      <motion.div style={{ opacity: likeOpacity }} className="absolute top-8 right-6 px-4 py-2 rounded-lg border-4 border-emerald-400 text-emerald-400 font-display font-black text-2xl rotate-12 pointer-events-none">
        LIKE
      </motion.div>
      <motion.div style={{ opacity: nopeOpacity }} className="absolute top-8 left-6 px-4 py-2 rounded-lg border-4 border-rose-500 text-rose-500 font-display font-black text-2xl -rotate-12 pointer-events-none">
        NOPE
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
        <h2 className="font-display font-black text-3xl leading-tight">
          {user.name}{user.age ? <span className="font-medium text-slate-300">, {user.age}</span> : null}
        </h2>
        {user.city && (
          <div className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-300">
            <MapPin size={14} /> {user.city}
          </div>
        )}
        {user.dating_bio && (
          <p className="mt-2 text-sm text-slate-200 line-clamp-3 leading-relaxed">{user.dating_bio}</p>
        )}
        {user.dating_interests?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {user.dating_interests.slice(0, 5).map((i) => (
              <span key={i} className="px-2 py-1 rounded-full bg-white/10 border border-white/15 text-xs">{i}</span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const MatchModal = ({ match, onClose }) => (
  <AnimatePresence>
    {match && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.85, y: 40 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.85, y: 40 }}
          className="max-w-sm w-full text-center relative"
          onClick={(e) => e.stopPropagation()}
          data-testid="match-modal"
        >
          <Sparkles size={48} className="mx-auto text-amber-400 mb-3" />
          <h2 className="font-display font-black text-5xl mb-2 bg-gradient-to-r from-rose-400 via-amber-400 to-rose-400 text-transparent bg-clip-text">
            It's a match!
          </h2>
          <p className="text-slate-300 mb-6">You and {match.other_user?.name} liked each other.</p>
          <div className="flex gap-3">
            <Button onClick={onClose} variant="outline" data-testid="match-keep-swiping" className="flex-1 h-12 border-white/15 bg-transparent hover:bg-white/5">
              Keep swiping
            </Button>
            <Link
              to={`/chat/${match.other_user?.id}`}
              data-testid="match-send-message"
              className="flex-1 h-12 rounded-md bg-rose-500 hover:bg-rose-400 text-white font-bold inline-flex items-center justify-center gap-1.5"
            >
              <MessageCircle size={16} /> Say hi
            </Link>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default function Dating() {
  const { user } = useAuth();
  const [deck, setDeck] = useState([]);
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dating/deck");
      setDeck(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const swipe = async (liked) => {
    const top = deck[deck.length - 1];
    if (!top) return;
    setDeck((d) => d.slice(0, -1));
    try {
      const { data } = await api.post("/dating/swipe", { target_user_id: top.id, liked });
      if (data.match) setMatch(data.match);
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const datingEnabled = user?.dating_enabled && (user?.age || 0) >= 18;

  return (
    <div className="min-h-screen bg-[#0B0F17] pb-28">
      <TopBar
        right={
          <Link data-testid="dating-matches-link" to="/matches" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs font-semibold hover:bg-white/5">
            <Users size={14} /> Matches
          </Link>
        }
      />

      <main className="max-w-md mx-auto pt-20 px-4">
        <div className="mb-4 text-center">
          <div className="overline text-rose-400 mb-1">MEET SOMEONE</div>
          <h1 className="font-display font-black text-3xl tracking-tight">Bring me as your +1.</h1>
        </div>

        {!datingEnabled ? (
          <div className="glass rounded-3xl p-8 text-center mt-8" data-testid="dating-disabled">
            <Heart size={40} className="mx-auto text-rose-400 mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">Enable your dating profile</h3>
            <p className="text-slate-400 text-sm mb-5">Add a photo, a short bio, and toggle dating on to start swiping.</p>
            <Link
              to="/profile"
              data-testid="dating-enable-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold text-sm"
            >
              Set up profile
            </Link>
          </div>
        ) : loading ? (
          <div className="relative w-full h-[520px] bg-[#131A26] rounded-3xl animate-pulse" />
        ) : deck.length === 0 ? (
          <div className="glass rounded-3xl p-8 text-center mt-8" data-testid="dating-empty">
            <Sparkles size={40} className="mx-auto text-amber-400 mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">You've seen everyone!</h3>
            <p className="text-slate-400 text-sm mb-5">Check back later for new profiles.</p>
            <button onClick={load} data-testid="dating-refresh" className="px-5 py-2.5 rounded-full border border-white/15 text-sm hover:bg-white/5">
              Refresh
            </button>
          </div>
        ) : (
          <>
            <div className="relative w-full h-[520px]">
              {deck.slice(-3).map((u, idx, arr) => (
                <Card
                  key={u.id}
                  user={u}
                  top={idx === arr.length - 1}
                  onSwipe={swipe}
                />
              ))}
            </div>
            <div className="flex justify-center gap-6 mt-6" data-testid="dating-actions">
              <button
                onClick={() => swipe(false)}
                data-testid="dating-nope"
                className="w-16 h-16 rounded-full bg-[#131A26] border border-white/15 text-rose-400 flex items-center justify-center hover:scale-105 hover:border-rose-500/40 transition-all shadow-lg"
              >
                <X size={28} strokeWidth={2.6} />
              </button>
              <button
                onClick={() => swipe(true)}
                data-testid="dating-like"
                className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 text-white flex items-center justify-center hover:scale-105 transition-all shadow-lg shadow-rose-500/40"
              >
                <Heart size={28} strokeWidth={2.6} fill="white" />
              </button>
            </div>
          </>
        )}
      </main>
      <MatchModal match={match} onClose={() => setMatch(null)} />
    </div>
  );
}
